import { Link } from "react-router-dom";
import { Target, Eye, Award, Users, Globe2, ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { STATS } from "@/lib/site-data";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative bg-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-100 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4">
              About R-Tech Computer
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.02]">
              Your trusted IT partner — <span className="text-red-600">on-ground</span> and on-call,
              every day of the year.
            </h1>
            <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-3xl">
              R-Tech Computer is an IT services company specializing in 24/7 infrastructure
              support and skilled technical manpower across India. We partner with enterprises,
              startups and growing businesses to keep their critical IT systems running at peak
              performance — wherever they operate.
            </p>
          </div>
        </div>
      </section>

      {/* Image + Story */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img
                src="https://images.unsplash.com/photo-1763128516808-785e80c1dd68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxJVCUyMHN1cHBvcnQlMjBlbmdpbmVlciUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzgwODYxNjR8MA&ixlib=rb-4.1.0&q=85"
                alt="IT Engineer at work"
                className="w-full h-[520px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4">Our story</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 leading-tight">
              Engineered to keep your business connected 24/7.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Built by IT professionals who've spent years inside data centers and enterprise
              networks, R-Tech exists for one reason — to make sure your infrastructure never
              becomes a bottleneck. From routers and switches, to virtualization stacks, to
              full Microsoft ecosystems — our engineers handle the complex so your team can
              focus on the business.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {STATS.map((s) => (
                <div key={s.label} className="border border-slate-200 rounded-xl bg-white p-5">
                  <div className="font-display text-2xl font-black text-slate-900">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-white py-20 lg:py-28" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-6 lg:gap-8">
          <div className="bg-blue-900 text-white rounded-2xl p-10 lg:p-12 relative overflow-hidden" data-testid="mission-card">
            <div className="absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-700/40 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center mb-6">
                <Target className="h-6 w-6" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-200 mb-3">
                Our Mission
              </p>
              <h3 className="font-display text-3xl lg:text-4xl font-black tracking-tighter leading-tight">
                Deliver seamless IT infrastructure support — every hour, every city.
              </h3>
              <p className="mt-5 text-blue-100/90 leading-relaxed">
                We exist to remove friction from enterprise IT operations through reliable
                engineering, disciplined SLAs and a relentless focus on uptime.
              </p>
            </div>
          </div>

          <div className="bg-slate-950 text-white rounded-2xl p-10 lg:p-12 relative overflow-hidden grid-bg-dark" data-testid="vision-card">
            <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-red-600/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="h-12 w-12 rounded-lg bg-red-600/15 text-red-400 flex items-center justify-center mb-6">
                <Eye className="h-6 w-6" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-300 mb-3">
                Our Vision
              </p>
              <h3 className="font-display text-3xl lg:text-4xl font-black tracking-tighter leading-tight">
                Become India's most trusted partner for enterprise IT support.
              </h3>
              <p className="mt-5 text-slate-300 leading-relaxed">
                A company every IT leader names first when they need engineering rigor,
                pan-India reach, and dependable execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            eyebrow="Our principles"
            title="What we stand for."
            description="The values that drive every engagement, every ticket, every site visit."
            testid="values-header"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { icon: Award, title: "Engineering Excellence", desc: "Certified expertise across networking, virtualization and Microsoft platforms." },
              { icon: Users, title: "People First", desc: "Skilled engineers who treat your infrastructure as their own." },
              { icon: Globe2, title: "Pan-India Reach", desc: "On-ground presence in major Indian cities for true field engineering." },
            ].map(({ icon: Ic, title, desc }) => (
              <div key={title} className="bg-white border border-slate-200 rounded-xl p-8" data-testid={`value-${title.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="h-12 w-12 rounded-lg bg-blue-50 text-blue-900 flex items-center justify-center mb-5">
                  <Ic className="h-6 w-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/contact"
              data-testid="about-cta-link"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-4 rounded-md font-semibold transition-all hover:-translate-y-0.5"
            >
              Partner with R-Tech <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
